/**
 * i18n — react-i18next initialization. English default, Arabic secondary.
 *
 * Pre-bundled translations (via i18nPreload.ts) are available immediately.
 * Feature namespaces (legal, auth, admin) still fetched via HTTP as fallback.
 * Imported at top of main.tsx before the app renders.
 *
 * Language: English by default. User changes persisted to localStorage via
 * the languageChanged listener. No LanguageDetector — avoids browser language
 * overriding the English default before React first renders.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-http-backend";
import { preloadedResources } from "./i18nPreload";

i18n
  .use(Backend)
  .use(initReactI18next)
  .init({
    lng: localStorage.getItem("i18nextLng") ?? "en", // Restore saved language or default to English
    fallbackLng: "en",
    supportedLngs: ["ar", "en"],
    defaultNS: "common",
    ns: ["common", "legal", "auth", "admin", "settings", "notifications", "titles", "pricing", "services"],
    backend: {
      // Base-aware so locale JSON resolves under subdirectory deploys, e.g.
      // /portal/locales/ar/common.json. BASE_URL is '/' in dev/Cloudflare.
      loadPath: `${import.meta.env.BASE_URL}locales/{{lng}}/{{ns}}.json`,
    },
    interpolation: { escapeValue: false },
    // Merge preloaded resources with HTTP fallback — preloaded is instant
    partialBundledLanguages: true,
    resources: preloadedResources,
  });

/** Set document direction + title whenever language changes. */
i18n.on("languageChanged", (lng) => {
  document.documentElement.dir = lng === "ar" ? "rtl" : "ltr";
  document.documentElement.lang = lng;
  localStorage.setItem("i18nextLng", lng);
  document.title = i18n.t("common:brandName", {
    defaultValue: document.title,
  });
});

/** Set initial title once i18n finishes loading the default namespace. */
i18n.on("initialized", () => {
  document.title = i18n.t("common:brandName", {
    defaultValue: document.title,
  });
});

/** Set initial direction on load — before first render */
const initialLng = localStorage.getItem("i18nextLng") ?? "en";
document.documentElement.dir = initialLng === "ar" ? "rtl" : "ltr";
document.documentElement.lang = initialLng;

export default i18n;